// MessengerPress — left panel: source PDF picker (issue #2392).
// Paginated, searchable list of recent FINAL PDFs eligible for
// messenger-press. Search hits order id, case id, deceased name, product
// name, and funeral home name. Page size is 10.

import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import colors from 'utils/styling/colors'
import {
  getMessengerPressSourcePdfs,
  uploadMessengerPressSource,
  type MessengerPressSourcePdf,
} from '@/api/messenger-press'

// Inline data-testid constants — kept in lockstep with the central
// registry at apps/mdl-frontend/tests/utils/selectors.ts so tests and
// components can reference the same identifiers without crossing the
// src ↔ tests tsconfig boundary.
const TID_LIST = 'messenger-press-source-pdf-list'
const TID_ITEM_PREFIX = 'messenger-press-source-pdf-item-'
const TID_SEARCH_INPUT = 'messenger-press-source-pdf-search'
const TID_PAGER_PREV = 'messenger-press-source-pdf-pager-prev'
const TID_PAGER_NEXT = 'messenger-press-source-pdf-pager-next'
const TID_UPLOAD_BUTTON = 'messenger-press-source-pdf-upload'
const TID_UPLOAD_INPUT = 'messenger-press-source-pdf-upload-input'

const MANUAL_UPLOAD_ID_PREFIX = 'manual-upload:'

// Callers can tell an uploaded PDF apart from a picked one by checking this
// prefix on `MessengerPressSourcePdf.id`. Downstream code that needs to know
// whether to use sourcePdfId vs sourcePdfUrl when submitting a job looks at
// this marker (see RecipeBuilder#handleSubmitJob).
export const isManualUploadPdf = (id: string): boolean =>
  id.startsWith(MANUAL_UPLOAD_ID_PREFIX)

const PAGE_SIZE = 10
// Debounce so we don't fire a request on every keystroke.
const SEARCH_DEBOUNCE_MS = 300

export type SourcePdfPickerProps = {
  selectedPdfId?: string
  onSelect: (pdf: MessengerPressSourcePdf) => void
}

export const SourcePdfPicker = ({
  selectedPdfId,
  onSelect,
}: SourcePdfPickerProps) => {
  const [pdfs, setPdfs] = useState<MessengerPressSourcePdf[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Reset to page 1 whenever the search term changes — otherwise a search
  // that returns fewer pages than the current page would render an empty
  // list and confuse the admin.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [searchInput])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await getMessengerPressSourcePdfs({
          q: searchTerm || undefined,
          page,
          pageSize: PAGE_SIZE,
        })
        if (cancelled) return
        setPdfs(response.data.pdfs)
        setTotal(response.data.total)
      } catch (err) {
        if (cancelled) return
        setError((err as Error).message ?? 'Failed to load source PDFs')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [searchTerm, page])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / PAGE_SIZE)),
    [total],
  )
  const canPrev = page > 1
  const canNext = page < totalPages

  const handleUploadClick = () => fileInputRef.current?.click()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset the input so picking the same filename twice fires change again.
    e.target.value = ''
    if (!file) return
    try {
      setUploading(true)
      setUploadError(null)
      const res = await uploadMessengerPressSource(file)
      const synthetic: MessengerPressSourcePdf = {
        id: `${MANUAL_UPLOAD_ID_PREFIX}${res.data.key}`,
        productId: '',
        productName: 'Manual upload',
        isM3: false,
        orderId: null,
        orderShort: null,
        caseName: file.name,
        funeralHomeName: null,
        createdAt: new Date().toISOString(),
        url: res.data.sourcePdfUrl,
      }
      onSelect(synthetic)
    } catch (err) {
      setUploadError((err as Error).message ?? 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Source PDFs</PanelTitle>
        <PanelSubtitle>
          Final print PDFs eligible for messenger-press. Search by order id,
          case id, deceased name, product, or funeral home.
        </PanelSubtitle>
      </PanelHeader>

      <UploadRow>
        <UploadButton
          type="button"
          data-testid={TID_UPLOAD_BUTTON}
          onClick={handleUploadClick}
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : 'Upload PDF'}
        </UploadButton>
        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          data-testid={TID_UPLOAD_INPUT}
          onChange={handleFileChange}
        />
      </UploadRow>
      {uploadError && <ErrorStatus>Upload error: {uploadError}</ErrorStatus>}

      <SearchInput
        type="search"
        data-testid={TID_SEARCH_INPUT}
        placeholder="Search…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
      />

      {loading && <Status>Loading source PDFs…</Status>}
      {error && <ErrorStatus>Error: {error}</ErrorStatus>}
      {!loading && !error && pdfs.length === 0 && (
        <Status>
          {searchTerm
            ? `No source PDFs match "${searchTerm}".`
            : 'No eligible source PDFs found.'}
        </Status>
      )}

      <List data-testid={TID_LIST}>
        {pdfs.map((pdf) => {
          const isSelected = pdf.id === selectedPdfId
          return (
            <ListItem
              key={pdf.id}
              type="button"
              data-testid={`${TID_ITEM_PREFIX}${pdf.id}`}
              $selected={isSelected}
              onClick={() => onSelect(pdf)}
            >
              <Thumbnail>
                {pdf.thumbnailUrl ? (
                  <img src={pdf.thumbnailUrl} alt="PDF thumbnail" />
                ) : (
                  <ThumbnailPlaceholder>PDF</ThumbnailPlaceholder>
                )}
              </Thumbnail>
              <Meta>
                <PrimaryLine>
                  {pdf.caseName ?? '—'} <Muted>· {pdf.productName}</Muted>
                </PrimaryLine>
                <MetaLine>{pdf.funeralHomeName ?? 'No funeral home'}</MetaLine>
                <MetaLine>
                  {new Date(pdf.createdAt).toLocaleString()}
                  {pdf.orderShort ? ` · order ${pdf.orderShort}…` : ''}
                </MetaLine>
              </Meta>
            </ListItem>
          )
        })}
      </List>

      {!loading && total > 0 && (
        <Pager>
          <PagerButton
            type="button"
            data-testid={TID_PAGER_PREV}
            disabled={!canPrev}
            onClick={() => canPrev && setPage((p) => p - 1)}
          >
            ‹ Prev
          </PagerButton>
          <PagerLabel>
            Page {page} of {totalPages} · {total} total
          </PagerLabel>
          <PagerButton
            type="button"
            data-testid={TID_PAGER_NEXT}
            disabled={!canNext}
            onClick={() => canNext && setPage((p) => p + 1)}
          >
            Next ›
          </PagerButton>
        </Pager>
      )}
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors('palePrimary')};
  border-radius: 12px;
  padding: 20px;
  /* Fill the grid row envelope and let the inner List own the scroll. */
  height: 100%;
  min-height: 0;
  overflow: hidden;
  gap: 12px;
`

const PanelHeader = styled.div`
  margin-bottom: 4px;
`

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: ${colors('almostBlack')};
`

const PanelSubtitle = styled.p`
  font-size: 13px;
  margin: 0;
  color: ${colors('gray2')};
`

const SearchInput = styled.input`
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${colors('gray4')};
  background: ${colors('white')};
  color: ${colors('almostBlack')};
  font-size: 13px;

  &::placeholder {
    color: ${colors('gray3')};
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`

const ListItem = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 2px solid
    ${({ $selected }) => ($selected ? colors('brand') : 'transparent')};
  background: ${({ $selected }) =>
    $selected ? colors('paleSecondary') : colors('white')};
  cursor: pointer;
  text-align: left;
  transition: background 120ms ease;

  &:hover {
    background: ${colors('paleSecondary')};
  }
`

const Thumbnail = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${colors('gray5')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ThumbnailPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 11px;
  font-weight: 600;
  color: ${colors('gray2')};
`

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`

const PrimaryLine = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors('almostBlack')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Muted = styled.span`
  color: ${colors('gray2')};
  font-weight: 500;
`

const MetaLine = styled.div`
  font-size: 11px;
  color: ${colors('gray2')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Status = styled.div`
  padding: 12px;
  font-size: 13px;
  color: ${colors('gray2')};
`

const ErrorStatus = styled(Status)`
  color: ${colors('error')};
`

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid ${colors('gray4')};
`

const PagerButton = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid ${colors('gray4')};
  background: ${colors('white')};
  color: ${colors('almostBlack')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${colors('paleSecondary')};
  }

  &:disabled {
    color: ${colors('gray3')};
    cursor: not-allowed;
  }
`

const PagerLabel = styled.span`
  font-size: 12px;
  color: ${colors('gray2')};
`

const UploadRow = styled.div`
  display: flex;
  gap: 8px;
`

const UploadButton = styled.button`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${colors('brand')};
  background: ${colors('brand')};
  color: ${colors('white')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const HiddenInput = styled.input`
  display: none;
`
